import * as React from 'react'
import { Box, Typography, Button, Modal, IconButton, Tabs, Tab, FormControl, InputLabel, Select, MenuItem, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Collapse, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckIcon from '@mui/icons-material/Check'
import { QRCodeSVG } from 'qrcode.react'
import { useProviderContext } from '../../context/ProviderContext'
import { useGetIdentity } from 'react-admin'
import { useNavigate, useLocation } from 'react-router-dom'
import KitRequestForm from './KitRequestForm'
import KitRequestList from './KitRequestList'
import { client } from '../../providers/data/client'

const KitRequests = () => {
  const { providerInfo } = useProviderContext()
  const { identity } = useGetIdentity()
  const navigate = useNavigate()
  const location = useLocation()
  const [modalOpen, setModalOpen] = React.useState(false)
  const [publicLinkModalOpen, setPublicLinkModalOpen] = React.useState(false)
  const [publicLink, setPublicLink] = React.useState('')
  const [isGeneratingLink, setIsGeneratingLink] = React.useState(false)

  const queryParams = new URLSearchParams(location.search)
  const initialTab = queryParams.get('tab') === 'request_history' ? 1 : 0
  const [tabIndex, setTabIndex] = React.useState(initialTab)

  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    const tab = params.get('tab') === 'request_history' ? 1 : 0
    if (tab !== tabIndex) {
      setTabIndex(tab)
    }
  }, [location.search])

  // @deprecated - Modal form functionality is scheduled for future deletion.
  const handleOpenModal = () => {
    setModalOpen(true)
  }
  // @deprecated - Modal form functionality is scheduled for future deletion.
  const handleCloseModal = () => {
    setModalOpen(false)
  }
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue)
    const tabName = newValue === 1 ? 'request_history' : 'request_form'
    navigate(`?tab=${tabName}`, { replace: true })
  }

  const hasKitRequestsAccess =
    !!providerInfo.kitRequestsEnabled ||
    !!identity?.kitRequests ||
    localStorage.getItem('provider_portal_kit_requests') === 'true'

  const [providerGroups, setProviderGroups] = React.useState([])
  const [selectedGroupId, setSelectedGroupId] = React.useState('')
  const [isLoadingGroups, setIsLoadingGroups] = React.useState(true)

  React.useEffect(() => {
    const fetchGroups = async () => {
      setIsLoadingGroups(true)
      try {
        const response = await client().get('/provider-portal/provider-groups')
        if (response.data && response.data.data) {
          const groups = response.data.data
          setProviderGroups(groups)
          if (groups.length > 0) {
            setSelectedGroupId(groups[0].id)
          }
        }
      } catch (err) {
        console.error('Error fetching provider groups:', err)
      } finally {
        setIsLoadingGroups(false)
      }
    }
    if (hasKitRequestsAccess) {
      fetchGroups()
    }
  }, [hasKitRequestsAccess])

  const [copied, setCopied] = React.useState(false)
  const [linkError, setLinkError] = React.useState('')

  React.useEffect(() => {
    if (!selectedGroupId) {
      setPublicLink('')
      setLinkError('')
      return
    }

    let isMounted = true
    const fetchLink = async () => {
      setIsGeneratingLink(true)
      setLinkError('')
      try {
        const response = await client().get(`/provider-portal/kit-requests/public-link?_providerGroupId=${selectedGroupId}`)
        if (isMounted) {
          setPublicLink(response.data.link)
        }
      } catch (err) {
        console.error('Error auto-generating public link:', err)
        if (isMounted) {
          setLinkError(err.response?.data?.message || 'Failed to generate public link.')
        }
      } finally {
        if (isMounted) {
          setIsGeneratingLink(false)
        }
      }
    }

    fetchLink()

    return () => {
      isMounted = false
    }
  }, [selectedGroupId])

  const handleCopyLink = () => {
    if (publicLink) {
      navigator.clipboard.writeText(publicLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // @deprecated - Generate Public Link dialog functionality is scheduled for future deletion.
  // The public link and QR code are now automatically generated and displayed below the dropdown.
  const handleGeneratePublicLink = async () => {
    setIsGeneratingLink(true)
    try {
      const response = await client().get(`/provider-portal/kit-requests/public-link?_providerGroupId=${selectedGroupId}`)
      setPublicLink(response.data.link)
      setPublicLinkModalOpen(true)
    } catch (err) {
      console.error('Error generating public link:', err)
      alert(err.response?.data?.message || 'Failed to generate public link.')
    } finally {
      setIsGeneratingLink(false)
    }
  }

  // Common header and description.  Include this for all viewers, even those without access, since it provides important context about the kit request process and instructions for how to gain access if they don't have it.
  //
  // Header: Kit Requests
  // Description: Kit Requests gives you the ability to order testing kits to be sent to your patients approximately 11 weeks after vasectomy. Use the Kit Request options below to create a request.  The request may be entered directly by you, or a tablet/iPad can be given to the patient to complete the form at checkout.
  const commonIntro = (
    <>
      <Typography variant="h4" gutterBottom>
        Kit Requests
      </Typography>
      <Typography paragraph>
        Kit Requests gives you the ability to order testing kits to be sent to your patients. Use the Kit Request options below to create a request. The request may be entered directly by provider office staff, or a tablet/iPad can be given to the patient to complete the form at checkout.
      </Typography>
    </>
  )

  if (!hasKitRequestsAccess) {
    return (
      <Box sx={{ p: 3 }}>
        {commonIntro}
        <Typography color="error">
          You do not have access to the Kit Requests feature. Please contact <a href="mailto:providers@malefromhome.com">providers@malefromhome.com</a> to enroll your account and providers you represent.
        </Typography>
      </Box>
    )
  }

  // // check if jotform id is configured (env var). Note: this is a temporary solution,
  // // making a single JotForm available to ALL provider-access users enrolled
  // // in kitRequests.
  // if (!jotformId) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       {commonIntro}
  //       <Typography color="error">
  //         Request Form not available. Please contact <a href="mailto:providers@malefromhome.com">providers@malefromhome.com</a>.
  //       </Typography>
  //     </Box>
  //   )
  // }

  return (
    <Box sx={{ p: 3 }}>
      {commonIntro}
      <Box component="ul" sx={{ pl: 4, mb: 2 }}>
        <li>Kits requested here are queued for shipment to the patient approximately 12 weeks after vasectomy</li>
        <li>Contact <a href="mailto:providers@malefromhome.com">providers@malefromhome.com</a> if any changes are needed following Kit Request submission</li>
        <li>Kits will be billed to your practice each month</li>
      </Box>
      {/* tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="New Request Form" value={0} />
          <Tab label="Request History" value={1} />
        </Tabs>
      </Box>
      {tabIndex === 0 && (
        <Box sx={{ border: '1px solid #ccc', borderRadius: 1, mb: 2 }}>
          {/* New Request Tab (Custom React Form) */}
          <Box sx={{ ml: 4, mr: 4, pt: 3, pb: 3 }}>
            <Typography variant="h5" gutterBottom>
              New Kit Request - Provider Office Instructions
            </Typography>
            <Box>
              <p>
                Request that the patient complete the form linked below and agree to MaleFromHome testing terms, which include:
              </p>
            </Box>
            <Box component="ul" sx={{ pl: 4, mb: 2, color: 'red' }}>
              <li>Male From Home, LLC will mail a post-vasectomy testing kit to the address provided approximately 12 weeks after vasectomy procedure</li>
              <li>The cost of this test, including shipping, is included in the total cost of the vasectomy as long as it is returned to MFH within 6 months of receiving it</li>
              <li>If you send the kit after 6 months of receiving it, you will be required to pay for testing out of pocket</li>
              <li>Patient may also be required to pay for additional test kits if any of the following apply: (a) testing kit is lost after patient receives it, (b) patient fails to follow the instructions regarding sample collection and return, (c) additional testing kits are necessary in the future</li>
              <li>This vasectomy test determines if sperm are present or not in the sample to determine if complete sperm clearance has occured</li>
              <li>If sperm continues to be present after initial (12 weeks) testing, a recommendation may be made to perform a second at-home test, or to collect a fresh sample for a local lab to determine motility</li>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{ ml: 6, mr: 6 }}>
                <p>
                  The form below opens full screen, and can be given to the patient to complete on iPad or tablet.
                </p>
              </Box>
              <Box sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
                <FormControl fullWidth disabled={isLoadingGroups}>
                  <InputLabel id="provider-group-select-label">
                    {isLoadingGroups ? 'Loading Provider Groups...' : 'Select Provider Group'}
                  </InputLabel>
                  <Select
                    labelId="provider-group-select-label"
                    value={selectedGroupId}
                    label={isLoadingGroups ? 'Loading Provider Groups...' : 'Select Provider Group'}
                    onChange={(e) => setSelectedGroupId(e.target.value)}
                    IconComponent={
                      isLoadingGroups
                        ? () => <CircularProgress size={20} sx={{ mr: 1, color: 'text.secondary' }} />
                        : undefined
                    }
                  >
                    {providerGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Automatically generated QR Code and Public Link when provider group is selected */}
              <Collapse in={!!selectedGroupId} sx={{ width: '100%' }}>
                <Box sx={{
                  mt: 1,
                  mb: 3,
                  p: 3,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  borderRadius: 2,
                  backgroundColor: 'rgba(248, 250, 252, 0.75)',
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2.5,
                  maxWidth: 450,
                  mx: 'auto',
                }}>
                  {isGeneratingLink ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 2 }}>
                      <CircularProgress size={36} sx={{ color: 'rgb(53, 171, 213)' }} />
                      <Typography variant="body2" color="text.secondary">
                        Generating QR code and public link...
                      </Typography>
                    </Box>
                  ) : linkError ? (
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                      <Typography color="error" variant="body2" gutterBottom>
                        {linkError}
                      </Typography>
                      <Button size="small" onClick={() => setSelectedGroupId(selectedGroupId)} sx={{ mt: 1 }}>
                        Retry
                      </Button>
                    </Box>
                  ) : publicLink ? (
                    <>
                      {/* QR Code Frame */}
                      <Box sx={{
                        p: 1.5,
                        bgcolor: '#ffffff',
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        <QRCodeSVG
                          value={publicLink}
                          size={160}
                          style={{ display: 'block' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
                          Scan to open form on mobile/tablet (expires in 2 weeks)
                        </Typography>
                      </Box>

                      {/* Action buttons */}
                      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                          variant="contained"
                          component="a"
                          href={publicLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600,
                            py: 1.2,
                            backgroundColor: 'rgb(53, 171, 213)',
                            border: '1px solid rgb(12, 66, 88)',
                            color: '#FFFFFF',
                            '&:hover': { backgroundColor: 'rgb(12, 66, 88)' },
                            boxShadow: 'none',
                          }}
                        >
                          Open Form
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={handleCopyLink}
                          startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600,
                            py: 1.2,
                            borderColor: 'rgb(53, 171, 213)',
                            color: 'rgb(53, 171, 213)',
                            '&:hover': {
                              borderColor: 'rgb(12, 66, 88)',
                              backgroundColor: 'rgba(53, 171, 213, 0.04)',
                            },
                          }}
                        >
                          {copied ? 'Copied!' : 'Copy Form URL'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: -0.5 }}>
                          This URL is valid for 2 weeks.
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Collapse>

              {/* @deprecated - Launch Kit Request Form button is scheduled for deletion. */}
              {/* <Button
                variant="contained"
                onClick={handleOpenModal}
                disabled={!selectedGroupId}
                sx={{
                  mt: 2,
                  textTransform: 'uppercase',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '12px 12px',
                  fontFamily: 'inherit',
                  backgroundColor: 'rgb(53, 171, 213)',
                  border: '1px solid rgb(12, 66, 88)',
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: 'rgb(12, 66, 88)' },
                }}
              >
                Launch Kit Request Form
              </Button> */}
              {/* @deprecated - Generate Public Link button is scheduled for deletion. */}
              {/* <Button
                variant="outlined"
                onClick={handleGeneratePublicLink}
                disabled={!selectedGroupId || isGeneratingLink}
                sx={{
                  mt: 2,
                  ml: 2,
                  textTransform: 'uppercase',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '12px 12px',
                  fontFamily: 'inherit',
                }}
              >
                {isGeneratingLink ? <CircularProgress size={24} /> : 'Generate Public Link'}
              </Button> */}
            </Box>
          </Box>
        </Box>
      )}

      {tabIndex === 1 && (
        <Box>
          <KitRequestList onEdit={(record) => navigate(`${record.id}/edit`)} />
        </Box>
      )}

      {/* @deprecated - Modal form is scheduled for deletion. */}
      {/* full-screen modal overlay with form */}
      {/* <Modal
        open={modalOpen}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleCloseModal();
          }
        }}
        disableEscapeKeyDown
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 'calc(100vw - 32px)',
            height: 'calc(100vh - 32px)',
            maxWidth: '1200px',
            backgroundColor: 'white',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 24,
          }}
        > */}
      {/* header with title and close button */}
      {/* <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 48,
              bgcolor: 'rgb(12, 66, 88)',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              zIndex: 20,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 'bold' }}>
              Post-vasectomy Testing Kit Request Form
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: '#FFFFFF',
                '&:hover': { backgroundColor: '#ff9c00' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box> */}

      {/* custom component, loads when modal is open */}
      {/* {modalOpen && (
            <Box
              sx={{
                position: 'absolute',
                top: 48,
                left: 0,
                width: '100%',
                height: 'calc(100% - 48px)',
                bgcolor: 'background.paper',
                overflow: 'auto',
                zIndex: 10,
              }}
            >
              <KitRequestForm
                onCancel={handleCloseModal}
                onSuccess={() => { }}
                providerAccessId={identity?.id || identity?.email}
                identity={identity}
                providerGroupId={selectedGroupId}
              />
            </Box>
          )} */}
      {/* </Box>
      </Modal> */}

      {/* @deprecated - Dialog for public link is scheduled for deletion. */}
      {/* Public Link Modal */}
      {/* <Dialog open={publicLinkModalOpen} onClose={() => setPublicLinkModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Public Kit Request Link</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" paragraph>
            You can share this link with patients or have them scan the QR code to fill out the Kit Request Form on their own devices.
          </Typography>
          {publicLink && (
            <Box mb={3} mt={2}>
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicLink)}`} 
                alt="QR Code for Public Form" 
              />
            </Box>
          )}
          <TextField 
            fullWidth 
            variant="outlined" 
            value={publicLink} 
            InputProps={{ readOnly: true }} 
            onClick={(e) => { e.target.select(); navigator.clipboard.writeText(publicLink); }}
            helperText="Click to copy to clipboard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublicLinkModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  )
}

export default KitRequests
